import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { message } from 'antd';
import moment from 'moment';
import { PageContainer } from '@ant-design/pro-layout';
import { useLoading } from 'hooks';
import { convertFilesForInitialValues, formatDatePickerValue } from 'utils';
import {
  UPDATE_PROJECT_APPLICATION,
  GET_MY_PROJECT_APPLICATIONS,
  GET_PROJECT_APPLICATION,
} from 'gql/projectApplications';
import FundingDetail from './FundingDetail';
import ProjectApplicationForm from './ProjectApplicationForm';
import EndorsementHistory from './EndorsementHistory';
import { getMax } from 'utils';
import { GET_ME } from 'gql/auth';

const maxEndorsementSequence = getMax('endorsementSequence');

const EditProjectApplication = ({ reducer: { dispatch } }) => {
  const { id } = useParams();
  const history = useHistory();
  const { data, loading } = useQuery(GET_PROJECT_APPLICATION, {
    variables: { id },
  });
  const { data: meData, loading: meLoading } = useQuery(GET_ME);
  const [updateProjectApplication, { loading: addLoading }] = useMutation(
    UPDATE_PROJECT_APPLICATION,
    {
      refetchQueries: [{ query: GET_MY_PROJECT_APPLICATIONS }],
    }
  );
  useLoading(addLoading);
  const onFinish = async (values) => {
    delete values.refNumber;
    const input = {
      ...values,
      startDate: values.startDate
        ? moment(values.startDate).format('YYYY-MM-DD')
        : null,
      endDate: values.endDate
        ? moment(values.endDate).format('YYYY-MM-DD')
        : null,
      id: id,
      proposal: values.proposal?.[0]?.id,
    };
    await updateProjectApplication({
      variables: {
        input,
      },
    });
    message.success('Funding application saved successfully');
    dispatch({ type: 'SUBMIT' });
  };
  const onSave = async (form) => {
    const values = form.getFieldsValue();
    delete values.refNumber;
    const input = {
      ...values,
      startDate: values.startDate
        ? moment(values.startDate).format('YYYY-MM-DD')
        : null,
      endDate: values.endDate
        ? moment(values.endDate).format('YYYY-MM-DD')
        : null,
      id: id,
      proposal: values.proposal?.[0]?.id,
    };
    await updateProjectApplication({
      variables: {
        input,
      },
    });
    message.success('Funding application saved successfully');
    history.push('/project-applications/my');
  };
  const initialValues = data
    ? {
        title: data.projectApplication.title,
        refNumber:
          data.projectApplication.refNumber ?? 'Auto-generated by system',
        team: data.projectApplication.team.map((member) => ({
          teamMemberType: member.teamMemberType,
          name: member.name,
          department: member.department?.id,
          position: member?.position,
          email: member.email,
          netId: member.netId,
          user: member.user?.id,
          userFromHroStaff: member.userFromHroStaff?.id,
        })),
        tdgFund: data.projectApplication.tdgFund,
        matchingFund: data.projectApplication.matchingFund,
        matchingFundFundingSource:
          data.projectApplication.matchingFundFundingSource,
        startDate: formatDatePickerValue(data.projectApplication?.startDate),
        endDate: formatDatePickerValue(data.projectApplication?.endDate),
        proposal: convertFilesForInitialValues(
          data.projectApplication.proposal
        ),
        isResearchOriented: data.projectApplication.isResearchOriented,
      }
    : {};
  const initTab = maxEndorsementSequence(
    data?.projectApplication?.applicationEndorsements?.endorsements
  );
  const disabled =
    meLoading || loading
      ? true
      : meData?.me?.id !== data?.projectApplication?.user?.id;

  return (
    <PageContainer loading={loading}>
      <EndorsementHistory
        data={data?.projectApplication?.applicationEndorsements}
        initTab={initTab}
      />
      <FundingDetail data={data?.projectApplication.setting} />
      <ProjectApplicationForm
        initialValues={initialValues}
        disabled={disabled}
        loading={meLoading || loading}
        onFinish={onFinish}
        onSave={onSave}
        previousLevel="/project-applications/my"
      />
    </PageContainer>
  );
};

EditProjectApplication.propTypes = {};

export default EditProjectApplication;
